import React, { useState, useRef } from 'react';
import { uploadAPI } from '../../services/api';

function ImageUpload({ 
  onImageUploaded, 
  currentImage, 
  onImageRemoved, 
  onUpload,
  onCancel,
  suggestedKeys = [],
  pageName,
  title = "Menu Item Image" 
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(currentImage || null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      // Upload file
      const response = await uploadAPI.uploadImage(file);
      const imageData = response.data.data;

      // Clean up preview URL
      URL.revokeObjectURL(previewUrl);
      
      // Set final image URL
      setPreview(`http://localhost:3001${imageData.url}`);

      // Notify parent component
      if (onImageUploaded) {
        onImageUploaded(imageData.url);
      }

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image');
      setPreview(currentImage || null);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemoveImage = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="image-upload-component">
      <label className="form-label">Menu Item Image</label>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Image Preview or Upload Area */}
      {preview ? (
        <div className="image-preview">
          <img 
            src={preview} 
            alt="Menu item preview" 
            className="preview-image"
          />
          <div className="image-overlay">
            <button
              type="button"
              className="btn btn-sm btn-primary me-2"
              onClick={openFileDialog}
              disabled={uploading}
            >
              <i className="fas fa-edit me-1"></i>
              Change
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={handleRemoveImage}
              disabled={uploading}
            >
              <i className="fas fa-trash me-1"></i>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFileDialog}
        >
          {uploading ? (
            <div className="upload-progress">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Uploading...</span>
              </div>
              <p className="mt-2 mb-0">Uploading image...</p>
            </div>
          ) : (
            <div className="upload-prompt">
              <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
              <h6>Upload Menu Item Image</h6>
              <p className="text-muted mb-2">
                Drag & drop an image here, or click to select
              </p>
              <small className="text-muted">
                JPEG, PNG, GIF, WebP • Max 5MB
              </small>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger mt-2">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Help Text */}
      <div className="form-text">
        Upload a high-quality image of your menu item. Recommended size: 800x600px or larger.
      </div>

      <style jsx>{`
        .image-upload-component {
          margin-bottom: 1rem;
        }

        .upload-dropzone {
          border: 2px dashed #dee2e6;
          border-radius: 15px;
          padding: 3rem 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background-color: #f8f9fa;
        }

        .upload-dropzone:hover {
          border-color: #c9302c;
          background-color: #fff;
        }

        .upload-dropzone.drag-over {
          border-color: #c9302c;
          background-color: rgba(201, 48, 44, 0.05);
        }

        .upload-dropzone.uploading {
          pointer-events: none;
          opacity: 0.7;
        }

        .upload-prompt h6 {
          color: #495057;
          margin-bottom: 0.5rem;
        }

        .upload-progress {
          color: #6c757d;
        }

        .image-preview {
          position: relative;
          border-radius: 15px;
          overflow: hidden;
          background-color: #f8f9fa;
          border: 2px solid #dee2e6;
        }

        .preview-image {
          width: 100%;
          height: 200px;
          object-fit: cover;
          display: block;
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .image-preview:hover .image-overlay {
          opacity: 1;
        }

        .form-text {
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #6c757d;
        }
      `}</style>
    </div>
  );
}

export default ImageUpload;